import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AppModal, AppRemixIcon } from '@/layout/AppComponents';
import Moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import colors from '../../../../../constants/colors';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import { getCAFilterInitialValues } from '../../../../../minions/forms';
import GoToMarker from './Elements/GoToMarker';
import Translator from '../../../../components/helpers/Translator';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import EmailNotificationSettings from '../../../../screenSpecifics/Modules/CorrectiveActions/EmailNotifications/EmailNotificationSettings';
import useIsMounted from '../../../../components/hooks/useIsMounted';

export default function NavBarCorrectiveActionsTask() {
  const { moduleBridgeHex, view } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const currentFilters = useSelector((state) => state.currentPage?.filters);
  const moduleJsonSettings = useSelector(
    (s) => s.module[moduleBridgeHex]?.jsonSettings
  );
  const [navbarInfo, setNavbarInfo] = useState();
  const [showCustomNotificationModal, setShowCustomNotificationModal] =
    useState(false);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > GET NARVBAR INFO
  const getNavbarInfo = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_correctiveActions_task_list_getNavbarInfo'
          ),
          { moduleBridgeHex, view }
        )
        .then((r) => {
          if (isMounted.current) {
            setNavbarInfo(r.data);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, view]
  );

  useEffect(() => {
    getNavbarInfo();
  }, [view]);
  // < GET NARVBAR INFO

  // > GENERATE FILTER HEX CODE
  const generateFilterHexCode = useCallback(
    async (values) => {
      const filters = {
        ...getCAFilterInitialValues(),
        ...values,
      };

      await axios
        .post(
          dispatch(
              'api_r_user_module_correctiveActions_task_list_generateFilterHexCode'
          ),
          { moduleBridgeHex, filters }
        )
        .then((r) => {
          navigate(
            path({
              name: 'user_module_correctiveActions_task_list',
              params: {
                moduleBridgeHex,
                view,
                page: 1,
                filtersHex: r.data.hexCode,
              },
            })
          );
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, currentFilters, view]
  );
  // < GENERATE FILTER HEX CODE

  const getExpiredBeforeFifteenDaysTasks = () => {
    generateFilterHexCode({
      limitStampFrom: Moment().unix(),
      limitStampTo: Moment().add(15, 'days').unix(),
    });
  };

  const getExpiredTaks = () => {
    generateFilterHexCode({
      limitStampTo: Moment().unix(),
    });
  };

  return (
    <Container>
      <GoToMarker />
      <NavbarIcon
        title="Cumple en menos de 15 días"
        className={!navbarInfo?.expiredBeforeFifteenDaysTasks ? 'off' : ''}
        onClick={getExpiredBeforeFifteenDaysTasks}
      >
        <AppRemixIcon
          icon="time"
          size={20}
          filled={navbarInfo?.expiredBeforeThreeMonthsObligations}
          color={navbarInfo?.expiredBeforeFifteenDaysTasks && colors.state2}
        />
        {navbarInfo?.expiredBeforeFifteenDaysTasks > 0 && (
          <>
            {navbarInfo.expiredBeforeFifteenDaysTasks > 99 ? (
              <span className="badge">+99</span>
            ) : (
              <span className="badge">
                {navbarInfo.expiredBeforeFifteenDaysTasks}
              </span>
            )}
          </>
        )}
      </NavbarIcon>
      <NavbarIcon
        title="Plazo vencido"
        className={!navbarInfo?.expiredTasks ? 'off' : ''}
        onClick={getExpiredTaks}
      >
        <AppRemixIcon
          icon="alert"
          size={20}
          filled={navbarInfo?.expiredTasks}
          color={navbarInfo?.expiredTasks && colors.danger}
        />
        {navbarInfo?.expiredTasks > 0 && (
          <>
            {navbarInfo.expiredTasks > 99 ? (
              <span className="badge">+99</span>
            ) : (
              <span className="badge">{navbarInfo.expiredTasks}</span>
            )}
          </>
        )}
      </NavbarIcon>
      {moduleJsonSettings?.calendarEmail && (
        <>
          <NavbarIcon
            title={ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="settings" />
            )}
            className="pr-1"
            onClick={() => setShowCustomNotificationModal(true)}
          >
            <AppRemixIcon icon="mail-settings" size={20} color="white" />
          </NavbarIcon>
          <AppModal
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="modify.notifications"
              />
            )}
            show={showCustomNotificationModal}
            size="lg"
            onClose={() => setShowCustomNotificationModal(false)}
          >
            <EmailNotificationSettings
              type="ModuleBridge"
              onUpdate={() => setShowCustomNotificationModal(false)}
            />
          </AppModal>
        </>
      )}
    </Container>
  );
}

const Container = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 15px;
  color: white;
  li {
    display: inline-block;
    padding: 0 6px;
  }
  li + li {
    border-left: 2px solid ${colors.primaryLight};
  }
  li.off {
    opacity: 0.2;
  }
`;

const NavbarIcon = styled.li`
  position: relative;
  &:hover {
    cursor: pointer;
  }
  .badge {
    position: absolute;
    bottom: 0;
    right: 0;
    background-color: #f4f6f9;
    border-radius: 100px;
    color: ${colors.primary};
    font-size: 9px;
    padding: 2px;
    width: 15px;
    height: 15px;
    text-align: center;
  }
`;
